import * as React from "react";
import { UitkCard, UitkCardLink } from "@egds/react-core/cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "@egds/react-core/layout-flex";
import { UitkHeading, UitkText } from "@egds/react-core/text";
import { UitkImage } from "@egds/react-core/images";
import { UitkIcon, UitkIconSize } from "@egds/react-core/icons";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkSpacing } from "@egds/react-core/spacing";
import { UitkLayoutPosition } from "@egds/react-core/layout-position";
import { UitkList } from "@egds/react-core/list";
import { CarAddressMenu } from "components/flexComponents/Cars/CarAddressMenu";
import { Car, CarDisplayPrice, MileageInfo } from "typings/microserviceModels/cars-flex-module";
import { ICarOfferCardProps } from "./typings";
import { locKeys } from "./i10n";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";

export const getLocationText = (
  { pickUpLocation, airportCode, city, province, countryCode }: Car,
  formatText: (key: string, ...args: any[]) => string
): string => {
  if (city && countryCode) {
    return province
      ? formatText(locKeys.city, city, province, countryCode)
      : formatText(locKeys.cityWithoutProvince, city, countryCode);
  }

  if (pickUpLocation && airportCode) {
    return formatText(locKeys.airport, pickUpLocation, airportCode);
  }

  return "";
};

export const getMileageText = (
  { unlimitedMileage, freeDistance, distancePeriod, distanceUnit }: MileageInfo,
  formatText: (key: string, ...args: any[]) => string
): string => {
  if (unlimitedMileage) {
    return formatText(locKeys.mileageUnlimited);
  }

  if (freeDistance && distanceUnit && distancePeriod) {
    return formatText(
      `${locKeys.mileageLimitedPrefix}${distanceUnit.toLocaleLowerCase()}.${distancePeriod.toLocaleLowerCase()}`,
      freeDistance
    );
  }

  return formatText(locKeys.mileageLimited);
};

const CarDisplayPrice: React.FC<{ displayPrice: CarDisplayPrice }> = ({ displayPrice }) => {
  const { leadPrice, secondaryPrice, disclaimer } = displayPrice;

  if (disclaimer && secondaryPrice) {
    // Cali-compliant pricing view
    return (
      <>
        <UitkSpacing padding={{ blockstart: "two" }}>
          <UitkHeading size={4} tag="h4" align="right">
            {leadPrice.amount}
          </UitkHeading>
        </UitkSpacing>
        <UitkText size={100} align="right" spacing="one">
          {disclaimer}
        </UitkText>
        <UitkText size={100} weight="bold" align="right" spacing="one">
          {secondaryPrice.formatted}
        </UitkText>
      </>
    );
  }

  if (secondaryPrice) {
    // US POS non-Cali-compliant pricing view
    return (
      <>
        <UitkSpacing padding={{ blockstart: "two" }}>
          <UitkHeading size={4} tag="h4" align="right">
            {leadPrice.amount}
          </UitkHeading>
        </UitkSpacing>
        <UitkText size={200} align="right">
          {secondaryPrice.formatted}
        </UitkText>
      </>
    );
  }

  // Control view
  return (
    <>
      <UitkSpacing padding={{ blockstart: "two" }}>
        <UitkHeading size={4} tag="h4" align="right">
          {leadPrice.amount}
        </UitkHeading>
      </UitkSpacing>
      <UitkText size={200} align="right">
        {leadPrice.scope}
      </UitkText>
    </>
  );
};

export const CarOfferCard: React.FC<ICarOfferCardProps> = ({ carOffer, hideOptions }) => {
  const { formatText } = useLocalization();
  const {
    offerUrl,
    formattedDates,
    carType,
    carModel,
    passengerCapacity,
    vendorName,
    vendorLogo,
    imageUrl,
    displayPrice,
    offerCreated,
    hoursOrDays,
    offAirportPickUpLocationAddress,
    pickUpLocationDistance,
    mileageInfo,
  } = carOffer;

  const showPickupAddress =
    (!hideOptions || !hideOptions.hideOffAirportPickupLocationPWA) && offAirportPickUpLocationAddress;

  const vehicleAttributesList = [
    {
      content: formatText(locKeys.passengerCount, passengerCapacity),
      icon: <UitkIcon id="person" name="person" size={UitkIconSize.SMALL} />,
    },
  ];

  if (mileageInfo) {
    vehicleAttributesList.push({
      content: getMileageText(mileageInfo, formatText),
      icon: <UitkIcon id="speed" name="speed" size={UitkIconSize.SMALL} />,
    });
  }

  return (
    <UitkSpacing padding="three">
      <UitkCard overflow border>
        <UitkCardLink>
          <TrackedLink
            moduleName="CarsView"
            href={offerUrl}
            linkName="LandingPageCarDeals"
            rfrr={`page.TravelGuides.Cars.${carOffer.city}.TopCarDeal`}
          >
            {`${carType} ${carModel}`}
          </TrackedLink>
        </UitkCardLink>
        <UitkLayoutFlex wrap="wrap">
          <UitkLayoutFlexItem minWidth="full_width">
            <div>
              {formattedDates && (
                <UitkSpacing margin={{ blockend: "two" }}>
                  <UitkText size={200} weight="bold">
                    {formattedDates}
                  </UitkText>
                </UitkSpacing>
              )}
              <UitkHeading size={5} tag="h3">
                {carType}
              </UitkHeading>
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem shrink={1} grow={2} minWidth="twenty_four">
            <div>
              {carModel && (
                <UitkText spacing="one" size={300}>
                  {formatText(locKeys.carModel, carModel)}
                </UitkText>
              )}
              <UitkSpacing padding={{ blockend: "three", blockstart: "two" }}>
                <UitkList bullet="icon-alternate" data={vehicleAttributesList} />
              </UitkSpacing>
              {vendorLogo && (
                <UitkSpacing padding={{ blockend: "three" }}>
                  <div className="vendor-logo-container">
                    <UitkImage className="vendor-logo" src={vendorLogo} alt={vendorName} />
                  </div>
                </UitkSpacing>
              )}
              {showPickupAddress ? (
                <CarAddressMenu
                  locationText={getLocationText(carOffer, formatText)}
                  locationAddress={offAirportPickUpLocationAddress}
                  locationDistanceInfo={pickUpLocationDistance}
                />
              ) : (
                <UitkText size={300}>{getLocationText(carOffer, formatText)}</UitkText>
              )}
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem shrink={0} grow={1} minWidth="twenty_four">
            <UitkSpacing padding={{ blockstart: "one" }}>
              <div className="card-image-container">
                {imageUrl ? (
                  <UitkImage className="vehicle-image" src={imageUrl} alt={carModel} />
                ) : (
                  <UitkSpacing margin={{ inlinestart: "three" }}>
                    <div aria-hidden="true">
                      <UitkImage placeholderImage />
                    </div>
                  </UitkSpacing>
                )}
                {displayPrice && displayPrice.leadPrice && (
                  <UitkLayoutPosition type="absolute" position={{ bottom: "three", right: "three" }}>
                    <CarDisplayPrice displayPrice={displayPrice} />
                    {offerCreated && hoursOrDays && (
                      <UitkText size={100} align="right" spacing="one">
                        {formatText(locKeys.freshnessPrefix.concat(hoursOrDays), Number(offerCreated))}
                      </UitkText>
                    )}
                  </UitkLayoutPosition>
                )}
              </div>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkCard>
    </UitkSpacing>
  );
};
